import { FC, useEffect, useState } from "react";
import { App, Button, DatePicker, Descriptions, DescriptionsProps, Flex, Form, Input, InputNumber, notification, Spin, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { CloseOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { divided, multi, noRateAmount, plus } from "@/utils/calc";
import SText from "@/styles/SText";
import { Product } from "@/types/product";
import { PurchaseOrderAdd, PurchaseOrderAddItem, PurchaseOrder, Supplier } from "@/types/purchase";
import apis from "@/apis/purchase";

// Components
import DetailModal from "@/components/DetailModal";
import ProductDesc from "@/components/Desc/ProductDesc";
import { CountrySelect, CreditLevelSelect, CurrencySelect, DepartmentSelect } from "@/components/Selector";
import Dict from "@/components/Dict";
import { DictEnum } from "@/types/dict";
import SupplierSelect from "@/pages/Purchase/components/SupplierSelect";
import PurchaseOrderTypeSelect from "@/pages/Purchase/components/PurchaseOrderTypeSelect";
import SelectLaddersPrice from "@/pages/Purchase/components/SelectLaddersPrice";
import AddressSelect from "@/layout/HomeLayout/components/AddressSelect";

const { Title } = Typography;

export type FormType = "Add" | "Edit" | "Read";

// 表单数据类型
type PurchaseOrderFormDataItem = {
  itemType?: number;
  itemPriceId?: [string, string];
  itemPrice?: {
    expiredAt: number | null;
    product: Product | null;
    productId: string;
    price: string;
    count: number;
    discount: string;
    priceId: string;
  };
  itemProduct?: Product | null;
  itemCount?: number;
  itemTaxRate?: string;
  itemScheduledOrderTime?: dayjs.Dayjs | null;
  itemDeliveryDeadline?: dayjs.Dayjs | null;
  itemEstimateArrivalAt?: dayjs.Dayjs | null;
  itemDeliveredQuantity?: number;
  itemRemark?: string;
};
interface PurchaseOrderFormData {
  // 订单级别字段
  supplierId: string;
  supplier?: Supplier | null;
  currency: string;
  payMethod: string;
  remark: string;
  receiver?: string;
  receiverAddress?: string;
  receiverContact?: string;
  receiverMobile?: string;
  // 订单项列表
  items: Array<PurchaseOrderFormDataItem>;
}

interface PurchaseAddOrEditProps {
  open: boolean;
  formType: FormType;
  orderId?: string; // Edit/Read 模式下传入
  onClose: () => void;
  onSuccess: () => void;
}

const PurchaseAddOrEdit: FC<PurchaseAddOrEditProps> = ({
  open,
  formType,
  orderId,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const [formRef] = Form.useForm<PurchaseOrderFormData>();
  const [orderDetail, setOrderDetail] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [supplierId, setSupplierId] = useState<string>("");

  const isReadOnly = formType === "Read";
  const canEdit = !isReadOnly;

  // 加载订单详情
  useEffect(() => {
    if ((formType === "Edit" || formType === "Read") && orderId && open) {
      setLoading(true);
      apis.purchaseFetch(orderId)
        .then((data) => {
          setOrderDetail(data);
          const formData = formatOrderToFormData(data);
          formRef?.setFieldsValue(formData);
        })
        .catch(() => {
          notification.error({ message: t("common.fetchError") });
        })
        .finally(() => setLoading(false));
    }
  }, [formType, orderId, open]);

  // 关闭时重置
  useEffect(() => {
    if (!open) {
      setOrderDetail(null);
      setSupplierId("");
      formRef?.resetFields();
    }
  }, [open]);

  // 格式化订单数据为表单数据
  const formatOrderToFormData = (order: PurchaseOrder | null): PurchaseOrderFormData => {
    if (!order) {
      return {
        supplierId: "",
        currency: "",
        payMethod: "",
        remark: "",
        items: [{}],
      };
    }

    const formData: PurchaseOrderFormData = {
      supplierId: order.supplierId || "",
      supplier: order.supplier || null,
      currency: order.currency || "",
      payMethod: order.payMethod || "",
      remark: order.remark || "",
      receiver: order.receiver,
      receiverAddress: order.receiverAddress,
      receiverContact: order.receiverContact,
      receiverMobile: order.receiverMobile,
      items: order.items?.map((item) => ({
        itemType: item.type,
        itemPriceId: [item.productId, item.priceId],
        itemProduct: item.product,
        itemCount: item.count,
        itemTaxRate: item.taxRate,
        itemScheduledOrderTime: item.scheduledOrderTime ? dayjs(item.scheduledOrderTime) : null,
        itemDeliveryDeadline: item.deliveryDeadline ? dayjs(item.deliveryDeadline) : null,
        itemEstimateArrivalAt: item.estimateArrivalAt ? dayjs(item.estimateArrivalAt) : null,
        itemDeliveredQuantity: item.deliveredQuantity,
        itemRemark: item.remark,
        itemPrice: {
          expiredAt: item.price?.standardPrice?.expiredAt ?? null,
          product: item.price?.product || null,
          productId: item.price?.product?.id ?? "",
          price: item.price?.price || "",
          count: item.price?.count || 0,
          discount: item.price?.discount || "",
          priceId: item.price?.id || "",
        },
      })) || [{}],
    };

    setSupplierId(order.supplierId || "");
    return formData;
  };

  // 获取供应商描述信息
  const getSupplierDescriptions = (supplier: Supplier | null): DescriptionsProps["items"] => [
    {
      key: "code",
      label: t("purchase.code"),
      children: <SText text={supplier?.code || ""} />,
      span: 1,
    },
    {
      key: "shortName",
      label: t("purchase.shortName"),
      children: <SText text={supplier?.shortName || ""} />,
      span: 2,
    },
    {
      key: "name",
      label: t("purchase.name"),
      children: <SText text={supplier?.name || ""} />,
    },
    {
      key: "country",
      label: t("purchase.country"),
      children: <CountrySelect value={supplier?.country} readOnly />,
    },
    {
      key: "address",
      label: t("purchase.address"),
      children: <SText text={supplier?.address || ""} />,
    },
    {
      key: "zipCode",
      label: t("purchase.zipCode"),
      children: <SText text={supplier?.zipCode || ""} />,
    },
    {
      key: "email",
      label: t("purchase.email"),
      children: <SText text={supplier?.email || ""} />,
    },
    {
      key: "contact",
      label: t("purchase.contact"),
      children: <SText text={supplier?.contact || ""} />,
    },
    {
      key: "phone",
      label: t("purchase.phone"),
      children: <SText text={supplier?.phone || ""} />,
    },
    {
      key: "fax",
      label: t("purchase.fax"),
      children: <SText text={supplier?.fax || ""} />,
    },
    {
      key: "mobile",
      label: t("purchase.mobile"),
      children: <SText text={supplier?.mobile || ""} />,
    },
    {
      key: "account1",
      label: t("purchase.account1"),
      children: <SText text={supplier?.account1 || ""} />,
    },
    {
      key: "bank1",
      label: t("purchase.bank1"),
      children: <SText text={supplier?.bank1 || ""} />,
    },
    {
      key: "swiftCode1",
      label: t("purchase.swiftCode1"),
      children: <SText text={supplier?.swiftCode1 || ""} />,
    },
    {
      key: "account2",
      label: t("purchase.account2"),
      children: <SText text={supplier?.account2 || ""} />,
    },
    {
      key: "bank2",
      label: t("purchase.bank2"),
      children: <SText text={supplier?.bank2 || ""} />,
    },
    {
      key: "swiftCode2",
      label: t("purchase.swiftCode2"),
      children: <SText text={supplier?.swiftCode2 || ""} />,
    },
    {
      key: "currency",
      label: t("purchase.currency"),
      children: <CurrencySelect value={supplier?.currency} readOnly />,
    },
    {
      key: "creditLevel",
      label: t("purchase.creditLevel"),
      children: <CreditLevelSelect value={supplier?.creditLevel} readOnly />,
    },
    {
      key: "creditLine",
      label: t("purchase.creditLine"),
      children: <SText text={supplier?.creditLine || ""} />,
    },
    {
      key: "depositRate",
      label: t("purchase.depositRate"),
      children: <SText text={supplier?.depositRate || ""} suffix="%" />,
    },
    {
      key: "tradeTerm",
      label: t("purchase.tradeTerm"),
      children: <Dict type={DictEnum.PURCHASE_TRADE_TERM} value={supplier?.tradeTerm} readOnly />,
    },
    {
      key: "payMethod",
      label: t("purchase.payMethod"),
      children: <Dict type={DictEnum.PURCHASE_PAY_METHOD} value={supplier?.payMethod} readOnly />,
    },
    {
      key: "org",
      label: t("purchase.org"),
      children: <DepartmentSelect readOnly value={supplier?.orgId} />,
      span: 3,
    },
  ];

  // 提交表单
  const handleSubmit = async (values: PurchaseOrderFormData) => {
    if (submitting) return
    try {
      setSubmitting(true);

      if (formType === "Add") {
        // ========== 新增模式 ==========
        // 处理订单级别的公共字段
        const orderData: PurchaseOrderAdd = {
          currency: values.currency,
          payMethod: values.payMethod,
          remark: values.remark,
          receiver: values.receiver,
          receiverAddress: values.receiverAddress,
          receiverContact: values.receiverContact,
          receiverMobile: values.receiverMobile,
          amount: "0",
          rateAmount: "0",
          supplierId: values.supplierId,
          items: [],
        };

        // 处理订单项
        const items: PurchaseOrderAddItem[] = (values.items || []).map((item: PurchaseOrderFormDataItem) => {
          const rate = divided(item.itemTaxRate ?? "0", "100");
          const defaultCount = item.itemCount ?? 0;
          const price = item.itemPrice?.price || "";
          const rateAmountInfo = multi(defaultCount.toString(), price);
          const amountInfo = noRateAmount(rateAmountInfo, rate);          

          return {
            type: item.itemType === undefined ? 1 : item.itemType,
            sourceId: "",
            priceId: item.itemPriceId?.[1] ?? "",
            count: item.itemCount ?? 0,
            amount: amountInfo,
            rateAmount: rateAmountInfo,
            remark: item.itemRemark || '',
            scheduledOrderTime: item.itemScheduledOrderTime ? item.itemScheduledOrderTime.valueOf() : null,
            deliveryDeadline: item.itemDeliveryDeadline ? item.itemDeliveryDeadline.valueOf() : null,
            deliveredQuantity: item.itemDeliveredQuantity ?? 0,
            estimateArrivalAt: item.itemEstimateArrivalAt ? item.itemEstimateArrivalAt.valueOf() : null,
            taxRate: item.itemTaxRate ?? '',
          };
        });

        // 计算总金额
        const totalRateAmount = items.reduce((sum, item) => plus(sum, item.rateAmount || "0"), "0");
        const totalAmount = items.reduce((sum, item) => plus(sum, item.amount || "0"), "0");

        orderData.items = items;
        orderData.amount = totalAmount;
        orderData.rateAmount = totalRateAmount;

        await apis.add(orderData);
        notification.success({ message: t("common.addSuccess") });
      } else if (formType === "Edit" && orderId) {
        // ========== 编辑模式 ==========
        // 1. 先更新订单级别字段
        const orderUpdateData = {
          currency: values.currency,
          payMethod: values.payMethod,
          remark: values.remark,
          receiver: values.receiver,
          receiverAddress: values.receiverAddress,
          receiverContact: values.receiverContact,
          receiverMobile: values.receiverMobile,
        };

        // 更新订单
        await apis.update(orderId, {
          ...orderUpdateData,
          amount: "0",
          rateAmount: "0",
          supplierId: values.supplierId,
          items: [],
        });
        // 检查供应商是否变化
        const supplierChanged = orderDetail?.supplierId !== values.supplierId;

        // 如果供应商变了，需要先删除旧的订单项，然后添加新的
        if (supplierChanged && orderDetail?.items?.length) {
          // 删除所有旧的订单项
          for (const item of orderDetail.items) {
            await apis.itemDel(item.id);
          }
        }

        // 2. 处理订单项的增删改
        const newItems = values.items || [];
        const originalItems = orderDetail?.items || [];

        // 构建 ID 映射用于比较
        const processedIds = new Set<string>();

        for (let i = 0; i < newItems.length; i++) {
          const newItem = newItems[i];
          const originalItem = originalItems[i];

          const rate = divided(newItem.itemTaxRate ?? "0", "100");
          const defaultCount = newItem.itemCount ?? 0;
          const price = newItem.itemPrice?.price || "";
          const rateAmountInfo = multi(defaultCount.toString(), price);
          const amountInfo = noRateAmount(rateAmountInfo, rate);

          const itemData: PurchaseOrderAddItem = {
            type: newItem.itemType!,
            sourceId: "",
            priceId: newItem.itemPriceId?.[1] || "",
            count: newItem.itemCount!,
            amount: amountInfo,
            rateAmount: rateAmountInfo,
            remark: newItem.itemRemark!,
            scheduledOrderTime: newItem.itemScheduledOrderTime ? newItem.itemScheduledOrderTime.valueOf() : null,
            deliveryDeadline: newItem.itemDeliveryDeadline ? newItem.itemDeliveryDeadline.valueOf() : null,
            deliveredQuantity: newItem.itemDeliveredQuantity ?? 0,
            estimateArrivalAt: newItem.itemEstimateArrivalAt ? newItem.itemEstimateArrivalAt.valueOf() : null,
            taxRate: newItem.itemTaxRate!,
          };

          if (originalItem) {
            // 更新现有订单项
            await apis.itemUpdate(originalItem.id, itemData);
            processedIds.add(originalItem.id);
          } else {
            // 添加新订单项
            await apis.itemAdd(orderId, itemData);
          }
        }

        // 删除多余的订单项（如果新订单项数量少于原订单项数量）
        for (const originalItem of originalItems) {
          if (!processedIds.has(originalItem.id)) {
            await apis.itemDel(originalItem.id);
          }
        }

        notification.success({ message: t("common.updateSuccess") });
      }

      onSuccess();
      handleClose();
    } catch (error) {
      console.error("Form validation failed:", error);
    } finally {
      setSubmitting(false);
    }
  };

  // 关闭弹窗
  const handleClose = () => {
    formRef?.resetFields();
    setOrderDetail(null);
    setSupplierId("");
    onClose();
  };

  // 获取标题
  const getTitle = () => {
    const titleMap = {
      Add: t("purchase.addPurchaseOrder"),
      Edit: t("common.edit"),
      Read: t("common.detail"),
    };
    return titleMap[formType];
  };

  // 表单项配置
  const renderFormItems = () => {
    return (
      <>
        {/* ========== 订单信息 ========== */}
        <Title level={4} style={{ marginBottom: 12 }}>{t("purchase.orderList")}</Title>

        {/* ========== 供应商选择（Add 模式）========== */}
        {formType === "Add" && (
          <Form.Item
            label={t("purchase.supplier")}
            name="supplierId"
            rules={[{ required: true, message: t("purchase.plSupplier") }]}
            style={{ marginBottom: 12 }}
          >
            <SupplierSelect
              disabled={!canEdit}
              onChangeCustomer={(e) => {
                setSupplierId(e.id);
                formRef?.setFieldsValue({
                  supplier: e,
                  currency: e.currency,
                  payMethod: e.payMethod,
                });
                // 清空订单项列表
                formRef?.setFieldsValue({ items: [{}] });
              }}
            />
          </Form.Item>
        )}

        {/* 隐藏字段 - 供应商对象 */}
        <Form.Item name="supplier" hidden style={{ marginBottom: 0 }}>
          <Input />
        </Form.Item>

        {/* ========== 公共表单信息（收货地址等）========== */}
        <Form.Item
          label={t("purchase.receivingUnit")}
          name="receiver"
          style={{ marginBottom: 12 }}
        >
          <Input maxLength={120} showCount={canEdit} placeholder={t("purchase.receivingUnit")} disabled={!canEdit} />
        </Form.Item>

        <Form.Item
          label={t("purchase.receivingAddress")}
          name="receiverAddress"
          style={{ marginBottom: 12 }}
        >
          <AddressSelect
            onChange={(_, data) => {
              formRef?.setFieldsValue({
                receiverContact: data?.contact,
                receiverMobile: data?.mobile,
                currency: data?.label1
              });
            }}
          />
        </Form.Item>

        <Form.Item
          label={t("purchase.contact")}
          name="receiverContact"
          style={{ marginBottom: 12 }}
        >
          <Input maxLength={120} showCount={canEdit} placeholder={t("purchase.plContact")} disabled={!canEdit} />
        </Form.Item>

        <Form.Item
          label={t("purchase.phone1")}
          name="receiverMobile"
          style={{ marginBottom: 12 }}
        >
          <Input maxLength={120} showCount={canEdit} placeholder={t("purchase.phone1")} disabled={!canEdit} />
        </Form.Item>

        {/* ========== 订单级别信息 ========== */}
        <Form.Item
          label={t("purchase.currency")}
          name="currency"
          rules={[{ required: true, message: t("purchase.plCurrency") }]}
          style={{ marginBottom: 12 }}
        >
          <CurrencySelect readOnly={!canEdit} />
        </Form.Item>

        <Form.Item
          name="payMethod"
          label={t("purchase.payMethod")}
          rules={[{ required: true, message: t("purchase.plPayMethod") }]}
          style={{ marginBottom: 12 }}
        >
          <Dict type={DictEnum.PURCHASE_PAY_METHOD} readOnly={!canEdit} />
        </Form.Item>

        <Form.Item
          label={t("purchase.orderDesc")}
          name="remark"
          style={{ marginBottom: 12 }}
        >
          <Input.TextArea showCount={canEdit} maxLength={1000} placeholder={t("purchase.plOrderDesc")} autoSize={{ minRows: 2, maxRows: 4 }} disabled={!canEdit} />
        </Form.Item>

        {/* ========== 分隔线 ========== */}
        <Title level={4} style={{ marginBottom: 12 }}>{t("purchase.items")}</Title>

        {/* ========== 使用 Form.List 支持多个订单项 ========== */}
        <Form.List name="items">
          {(fields, { add, remove }) => {
            const items = formRef?.getFieldValue("items") || [];
            return (
              <>
                {fields.map(({ key, name, ...restField }) => {
                  const itemProduct = items[name]?.itemProduct;

                  return (
                    <div
                      key={key}
                      style={{
                        border: "1px solid #d9d9d9",
                        borderRadius: "8px",
                        padding: "12px",
                        paddingBottom: '18px',
                        marginBottom: "12px",
                      }}
                    >
                      {/* 序号和删除按钮 */}
                      <Flex justify="space-between" align="center" style={{ marginBottom: 8 }}>
                        <span style={{ fontWeight: 500, color: "rgba(0, 0, 0, 0.88)" }}>#{name + 1}</span>
                        {formType === "Add" && canEdit && fields.length > 1 && (
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<CloseOutlined />}
                            onClick={() => remove(name)}
                          />
                        )}
                      </Flex>

                      {/* 隐藏字段 */}
                      <Form.Item name={[name, "itemPrice"]} hidden>
                        <Input />
                      </Form.Item>

                      {/* 采购类型 */}
                      <Form.Item
                        label={t("purchase.purchaseType")}
                        name={[name, "itemType"]}
                        rules={[{ required: true, message: t("purchase.plPurchaseType") }]}
                        style={{ marginBottom: 8 }}
                      >
                        <PurchaseOrderTypeSelect readOnly={!canEdit} />
                      </Form.Item>

                      {/* 产品选择 */}
                      {formType === "Add" && canEdit ? (
                        <Form.Item
                          label={t("purchase.product")}
                          name={[name, "itemPriceId"]}
                          rules={[{ required: true, message: t("purchase.plProduct") }]}
                          style={{ marginBottom: 8 }}
                        >
                          <SelectLaddersPrice
                            supplierId={supplierId}
                            disabled={!canEdit}
                            disabledFetch={false}
                            onChange={(val, data) => {
                              const items = formRef?.getFieldValue("items") || [];
                              items[name] = {
                                ...items[name],
                                itemPrice: data,
                              };
                              formRef?.setFieldsValue({ items });
                            }}
                          />
                        </Form.Item>
                      ) : (
                        <div style={{ marginBottom: 8, display: "flex", alignItems: "center" }}>
                          <div style={{ color: "rgba(0, 0, 0, 0.85)", flexShrink: 0 }}>
                            <span style={{ color: "#ff4d4f", marginRight: 1 }}>*</span>
                            {t("purchase.product")}:
                          </div>
                          <Input
                            disabled
                            value={itemProduct?.name || ""}
                            placeholder={t("purchase.plProduct")}
                            style={{ flex: 1 }}
                          />
                        </div>
                      )}

                      {/* 产品描述 */}
                      {itemProduct && canEdit && (
                        <div style={{ marginBottom: 8 }}>
                          <ProductDesc product={itemProduct} />
                        </div>
                      )}

                      {/* 数量 */}
                      <Form.Item
                        label={t("purchase.productCount")}
                        name={[name, "itemCount"]}
                        rules={[{ required: true, message: t("purchase.plProductCount") }]}
                        style={{ marginBottom: 8 }}
                      >
                        <InputNumber
                          disabled={!canEdit}
                          placeholder={t("purchase.plProductCount")}
                          precision={0}
                          min={0}
                          style={{ width: "100%" }}
                        />
                      </Form.Item>

                      {/* 税率 */}
                      <Form.Item
                        label={t("purchase.taxRate")}
                        name={[name, "itemTaxRate"]}
                        style={{ marginBottom: 8 }}
                      >
                        <InputNumber
                          disabled={!canEdit}
                          placeholder={t("purchase.plTaxRate")}
                          min={0}
                          max={100}
                          style={{ width: "100%" }}
                          addonAfter={canEdit ? "%" : undefined}
                        />
                      </Form.Item>

                      {/* 预计下单时间 */}
                      <Form.Item
                        label={t("purchase.scheduledOrderTime")}
                        name={[name, "itemScheduledOrderTime"]}
                        style={{ marginBottom: 8 }}
                      >
                        <DatePicker placeholder={t("purchase.plScheduledOrderTime")} style={{ width: "100%" }} disabled={!canEdit} />
                      </Form.Item>

                      {/* 要求送货时间 */}
                      <Form.Item
                        label={t("purchase.deliveryDeadline")}
                        name={[name, "itemDeliveryDeadline"]}
                        style={{ marginBottom: 8 }}
                      >
                        <DatePicker placeholder={t("purchase.plDeliveryDeadline")} style={{ width: "100%" }} disabled={!canEdit} />
                      </Form.Item>

                      {/* 预计到货时间 */}
                      <Form.Item
                        label={t("purchase.estimateArrivalAt")}
                        name={[name, "itemEstimateArrivalAt"]}
                        style={{ marginBottom: 8 }}
                      >
                        <DatePicker placeholder={t("purchase.plEstimateArrivalAt")} style={{ width: "100%" }} disabled={!canEdit} />
                      </Form.Item>

                      {/* 已送达数量 - 仅 Edit/Read 模式 */}
                      {(formType === "Edit" || formType === "Read") && (
                        <Form.Item
                          label={t("purchase.deliveredQuantity")}
                          name={[name, "itemDeliveredQuantity"]}
                          style={{ marginBottom: 8 }}
                        >
                          <InputNumber
                            disabled={!canEdit}
                            placeholder={t("purchase.plDeliveredQuantity")}
                            precision={0}
                            min={0}
                            style={{ width: "100%" }}
                          />
                        </Form.Item>
                      )}

                      {/* 产品备注 */}
                      <Form.Item
                        label={t("purchase.productDesc")}
                        name={[name, "itemRemark"]}
                        style={{ marginBottom: 0 }}
                      >
                        <Input.TextArea
                          showCount={canEdit}
                          maxLength={1000}
                          placeholder={t("purchase.plProductDesc")}
                          autoSize={{ minRows: 2, maxRows: 4 }}
                          disabled={!canEdit}
                        />
                      </Form.Item>
                    </div>
                  );
                })}

                {/* 添加按钮 - 仅 Add 模式 */}
                {formType === "Add" && canEdit && (
                  <Button
                    type="dashed"
                    onClick={() => {
                      add({
                        itemType: undefined,
                        itemCount: 1,
                        itemTaxRate: "0",
                      });
                    }}
                    block
                    style={{ marginBottom: 16 }}
                  >
                    + {t("purchase.addItem")}
                  </Button>
                )}
              </>
            );
          }}
        </Form.List>

        {/* ========== 供应商信息展示 - 仅 Edit/Read 模式 ========== */}
        {(formType === "Edit" || formType === "Read") && orderDetail?.supplier && (
          <>
            <Title level={4}>{t("purchase.supplier")}</Title>
            <Descriptions items={getSupplierDescriptions(orderDetail.supplier)} />
          </>
        )}
      </>
    );
  };

  return (
    <DetailModal
      logBookId={orderDetail?.items?.[0]?.logBookId || ""}
      noLog={formType === "Add"}
      modalProps={{
        width: formType === "Add" ? 1000 : 1200,
        title: getTitle(),
        open,
        onCancel: handleClose,
        destroyOnClose: true,
        footer: null,
      }}
    >
      {loading && (formType === "Edit" || formType === "Read") ? (
        <Flex style={{ width: "100%", height: "100%" }} justify="center" align="center">
          <Spin />
        </Flex>
      ) : (
        <Flex vertical style={{ height: "70vh" }}>
          <div style={{ flex: 1, overflowY: "auto", padding: "0 16px" }}>
            <Form<PurchaseOrderFormData>
              form={formRef}
              layout="horizontal"
              labelWrap
              autoComplete="off"
              onFinish={handleSubmit}
              initialValues={{
                items: formType === "Add" ? [{}] : [],
                currency: "",
                payMethod: "",
              }}
              disabled={!canEdit}
            >
              {renderFormItems()}
            </Form>
          </div>
          {!isReadOnly && (
            <Flex style={{ padding: "16px", borderTop: "1px solid #f0f0f0" }} justify="flex-end" gap={8}>
              <Button onClick={handleClose} disabled={submitting}>
                {t("common.cancel")}
              </Button>
              <Button type="primary" onClick={formRef?.submit} loading={submitting}>
                {formType === "Add" ? t("common.add") : t("common.update")}
              </Button>
            </Flex>
          )}
        </Flex>
      )}
    </DetailModal>
  );
};

export default PurchaseAddOrEdit;
